import { useEffect, useMemo, useState } from "react";

import { useChunksByIds } from "src/views/datasets/services/ChunkServices";

/**
 * Global store for collecting chunk IDs that need fetching
 * This allows CitationLink components to register their IDs
 */
let globalChunkIdsToFetch: Set<number> = new Set();
let globalListeners: Array<() => void> = [];

/**
 * Register a chunk ID that needs fetching. Re-registering an id already in
 * the set is a no-op — a remounting CitationLink re-runs its registration
 * effect, and notifying listeners for a duplicate would re-render every
 * subscriber for nothing (#20423 remount loop).
 */
export const registerChunkIdToFetch = (chunkId: number) => {
  if (globalChunkIdsToFetch.has(chunkId)) return;
  globalChunkIdsToFetch.add(chunkId);
  globalListeners.forEach((listener) => listener());
};

/**
 * Hook to fetch missing citation sources for old conversations
 * Collects chunk IDs from citations that need fetching and uses React Query to get the data
 */
export const useCitationSources = (
  messageContent: string,
  dialogueId?: number | null,
) => {
  const [chunkIdsToFetch, setChunkIdsToFetch] = useState<number[]>([]);

  // Listen for chunk ID registrations
  useEffect(() => {
    // Clear the global store when message changes
    globalChunkIdsToFetch.clear();
    // Removed noisy debug log: "🔍 CITATION SOURCES: Cleared global store"

    const updateChunkIds = () => {
      // Keep the previous array identity when the ids are unchanged so React
      // bails out of the state update instead of re-rendering subscribers.
      setChunkIdsToFetch((prev) => {
        const ids = Array.from(globalChunkIdsToFetch);
        return prev.length === ids.length &&
          ids.every((id, i) => prev[i] === id)
          ? prev
          : ids;
      });
    };

    // Add listener
    globalListeners.push(updateChunkIds);

    // Initial check after a small delay to let citations render
    const timeoutId = setTimeout(() => {
      updateChunkIds();
    }, 150);

    return () => {
      // Remove listener
      globalListeners = globalListeners.filter((l) => l !== updateChunkIds);
      clearTimeout(timeoutId);
      // Clear on unmount
      globalChunkIdsToFetch.clear();
    };
  }, [messageContent]);

  // Fetch chunks using React Query
  const {
    data: fetchedChunks,
    isLoading,
    error,
    isSuccess,
  } = useChunksByIds(
    chunkIdsToFetch,
    {
      // Fail closed: without a dialogue id the backend can't apply the
      // citation allowlist, so never fire the fetch unscoped (#1900).
      enabled: chunkIdsToFetch.length > 0 && dialogueId != null,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
    // Scope to the dialogue (not message_id) so the backend enforces its
    // citation allowlist (#1900): the batched ids span messages, so they
    // can't be attributed to a single message.
    { dialogue_id: dialogueId ?? undefined },
  );

  // Create a lookup map for fetched chunks (API returns SearchResult format)
  const chunkMap = useMemo(() => {
    if (!fetchedChunks) return new Map();

    const map = new Map();
    fetchedChunks.forEach((chunk) => {
      // API returns SearchResult format
      const chunkId = chunk.data_chunk?.id;
      if (chunkId) {
        map.set(chunkId, chunk);
      }
    });
    return map;
  }, [fetchedChunks]);

  // Debug logging removed - was too noisy

  return {
    chunkMap,
    isLoading,
    error,
    isSuccess,
    chunkIdsToFetch,
    fetchedChunks, // Add this to return the actual fetched chunks array
  };
};
